import React, { useState } from 'react';

const moodColors = {
  1: '#F08080', 2: '#F4A261', 3: '#F7C948',
  4: '#7BC67E', 5: '#63C5DA', 6: '#63C5DA',
  7: '#7BC67E', 8: '#7BC67E', 9: '#F7C948', 10: '#F7C948'
};

const moodEmojis = {
  1: '😔', 2: '😟', 3: '😐', 4: '🙂', 5: '😊',
  6: '😊', 7: '😄', 8: '🤩', 9: '✨', 10: '🌟'
};

const sampleMoods = [6, 4, 7, 5, 8, 6, 7];
const sampleDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FDF8F5',
    display: 'flex',
  },
  sidebar: {
    width: '260px',
    background: '#FFFFFF',
    borderRight: '1px solid #EDE8E3',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flexShrink: 0,
  },
  logo: {
    fontSize: '26px',
    fontFamily: "'Playfair Display', serif",
    fontStyle: 'italic',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #F7C948, #F08080, #C3A8E1)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '32px',
    paddingLeft: '8px',
  },
  navItem: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '16px',
    fontSize: '15px',
    fontWeight: active ? 600 : 400,
    color: active ? '#2D2D2D' : '#6B6B6B',
    background: active ? '#FDF3E7' : 'transparent',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    transition: 'all 0.15s',
  }),
  navIcon: {
    fontSize: '20px',
    width: '24px',
    textAlign: 'center',
  },
  main: {
    flex: 1,
    padding: '40px 48px',
    overflowY: 'auto',
  },
  greeting: {
    fontSize: '32px',
    fontFamily: "'Playfair Display', serif",
    fontWeight: 600,
    color: '#2D2D2D',
    marginBottom: '8px',
  },
  date: {
    fontSize: '15px',
    color: '#A0A0A0',
    marginBottom: '40px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '32px',
  },
  card: {
    background: '#FFFFFF',
    borderRadius: '24px',
    padding: '28px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
    border: '1px solid #EDE8E3',
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#A0A0A0',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '20px',
  },
  moodRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
    height: '100px',
  },
  moodBar: (score) => ({
    flex: 1,
    background: moodColors[score] || '#EDE8E3',
    borderRadius: '8px',
    height: `${score * 10}%`,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '4px',
    fontSize: '14px',
    opacity: 0.85,
  }),
  dayLabel: {
    textAlign: 'center',
    fontSize: '11px',
    color: '#A0A0A0',
    marginTop: '8px',
  },
  companionCard: {
    background: 'linear-gradient(135deg, #FDF3E7 0%, #F9E8F0 100%)',
    borderRadius: '24px',
    padding: '28px',
    border: '1px solid rgba(247,201,72,0.2)',
    cursor: 'pointer',
  },
  companionEmoji: {
    fontSize: '40px',
    marginBottom: '16px',
  },
  companionTitle: {
    fontSize: '20px',
    fontWeight: 600,
    fontFamily: "'Playfair Display', serif",
    color: '#2D2D2D',
    marginBottom: '8px',
  },
  companionText: {
    fontSize: '14px',
    color: '#6B6B6B',
    lineHeight: 1.6,
    marginBottom: '20px',
  },
  companionBtn: {
    background: 'linear-gradient(135deg, #F7C948, #F4A261)',
    border: 'none',
    borderRadius: '999px',
    padding: '12px 24px',
    fontWeight: 600,
    fontSize: '14px',
    color: '#2D2D2D',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(247,201,72,0.35)',
  },
  quickActions: {
    display: 'flex',
    gap: '16px',
    marginBottom: '32px',
  },
  actionBtn: (gradient, shadow) => ({
    flex: 1,
    background: gradient,
    border: 'none',
    borderRadius: '20px',
    padding: '20px 24px',
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: shadow,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'transform 0.2s',
  }),
  actionIcon: {
    fontSize: '28px',
  },
  actionLabel: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#2D2D2D',
  },
  actionSub: {
    fontSize: '12px',
    color: 'rgba(45,45,45,0.6)',
    marginTop: '2px',
  },
};

const navItems = [
  { icon: '🏠', label: 'Home', page: 'dashboard' },
  { icon: '🧶', label: 'AI Companion', page: 'companion' },
  { icon: '📖', label: 'New Entry', page: 'journal' },
  { icon: '💛', label: 'Log Mood', page: 'mood' },
  { icon: '📖', label: 'My Diary', page: 'history' },
];

export default function Dashboard({ navigate }) {
  const [activePage, setActivePage] = useState('dashboard');

  const goTo = (page) => {
    setActivePage(page);
    navigate(page);
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div style={styles.page}>
      <div style={styles.sidebar}>
        <div style={styles.logo}>lifemap</div>
        {navItems.map((item) => (
          <button key={item.page} style={styles.navItem(activePage === item.page)} onClick={() => goTo(item.page)}>
            <span style={styles.navIcon}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>

      <div style={styles.main}>
        <div style={styles.greeting}>{greeting} 👋</div>
        <div style={styles.date}>{today}</div>

        <div style={styles.quickActions}>
          <button style={styles.actionBtn('linear-gradient(135deg, #FFF3CD, #FFE69C)', '0 4px 12px rgba(247,201,72,0.2)')} onClick={() => goTo('journal')}>
            <span style={styles.actionIcon}>📖</span>
            <div>
              <div style={styles.actionLabel}>Write Entry</div>
              <div style={styles.actionSub}>Capture your thoughts</div>
            </div>
          </button>
          <button style={styles.actionBtn('linear-gradient(135deg, #EDE8F7, #DDD5F0)', '0 4px 12px rgba(195,168,225,0.2)')} onClick={() => goTo('mood')}>
            <span style={styles.actionIcon}>💛</span>
            <div>
              <div style={styles.actionLabel}>Log Mood</div>
              <div style={styles.actionSub}>How are you feeling?</div>
            </div>
          </button>
          <button style={styles.actionBtn('linear-gradient(135deg, #E8F7ED, #C8EDD3)', '0 4px 12px rgba(123,198,126,0.2)')} onClick={() => goTo('companion')}>
            <span style={styles.actionIcon}>🧶</span>
            <div>
              <div style={styles.actionLabel}>Talk to Companion</div>
              <div style={styles.actionSub}>AI is here for you</div>
            </div>
          </button>
        </div>

        <div style={styles.grid}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Mood this week</div>
            <div style={styles.moodRow}>
              {sampleMoods.map((score, i) => (
                <div key={i} style={{ flex: 1 }}>
                  <div style={styles.moodBar(score)}>
                    <span>{moodEmojis[score]}</span>
                  </div>
                  <div style={styles.dayLabel}>{sampleDays[i]}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.companionCard} onClick={() => goTo('companion')}>
            <div style={styles.companionEmoji}>🧶</div>
            <div style={styles.companionTitle}>Your companion is ready</div>
            <div style={styles.companionText}>
              I've been thinking about you. Ready to talk, reflect, or just be heard?
            </div>
            <button style={styles.companionBtn} onClick={() => goTo('companion')}>
              Start conversation →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
